import React, { forwardRef } from 'react';
import { motion, useMotionValue, animate } from 'framer-motion';
import { theme } from 'antd';

export type SheetSnapPoint = 'collapsed' | 'media' | 'full';

/** Fallback/estimate for the collapsed peek height (handle + header block)
 * before it's actually measured (see peekHeight below) -- also what
 * ExploreMobileMap uses for its own map-controls clearance, where an exact
 * value isn't needed. */
export const SHEET_COLLAPSED_PEEK = 56;

/** Fraction of containerHeight the sheet's top edge sits at in 'media' --
 * exported so ExploreMobileMap can work out how much of the map that snap
 * point actually covers (for keeping a fresh search's fitted pin above the
 * sheet rather than behind it) without duplicating this as a second magic
 * number that could quietly drift out of sync with the one below. */
export const MEDIA_SNAP_RATIO = 0.48;

const SPRING = { type: 'spring' as const, damping: 32, stiffness: 300 };
const SNAP_ORDER: SheetSnapPoint[] = ['full', 'media', 'collapsed'];
// A flick past this speed (px/s) commits to the next stop in the flick's
// direction rather than whichever stop is numerically closest -- otherwise a
// fast, short flick starting near the middle of two stops can "lose" to the
// one it started at.
const FLICK_VELOCITY = 600;
// Below this much total pointer travel (px), a handle/header gesture is
// treated as a tap (see handleHeaderClick) rather than a drag -- generous
// enough to absorb ordinary touch jitter on a "still" tap.
const TAP_MOVE_THRESHOLD = 8;

interface SnapBottomSheetProps {
  /** Height (px) of the fixed-position area this sheet lives in. The sheet
   * itself always renders at this full height and is only ever *translated*
   * via `y` -- all three snap points are different y offsets on the same
   * element rather than three differently-sized ones, so nothing needs to
   * reflow when it moves. */
  containerHeight: number;
  /** Height (px) of the fixed app-level header that sits above this sheet
   * with a higher z-index. Baked directly into the 'full' snap offset
   * (rather than reserved as top padding on the sheet itself) so 'full'
   * still slides the sheet's top edge right up against that header, but
   * 'media'/'collapsed' don't inherit that same offset as dead space above
   * the handle -- see this component's own doc comment below. */
  headerClearance: number;
  snapPoint: SheetSnapPoint;
  onSnapPointChange: (point: SheetSnapPoint) => void;
  /** Fades this sheet out entirely (opacity + its own pointer-events, not
   * unmounted -- scroll position, snap point and drag state all survive)
   * while a map pin's popup is open, since the two don't coexist. Restoring
   * it (false again) needs no extra bookkeeping: snapPoint itself was never
   * touched while hidden, so it simply reappears wherever it already was. */
  hidden?: boolean;
  /** Fires whenever the list's own scrollTop crosses to/from 0, so a caller
   * can sync other UI (e.g. a floating "Map" button) to the same signal
   * that drives this sheet's own loose handle (see listScrolled below). */
  onListScrolledChange?: (scrolled: boolean) => void;
  /** Rendered below the drag handle, as the first item of the sheet's own
   * scrollable content -- typically the "N spaces found" row. Interactive
   * children are excluded automatically from starting a drag (see
   * handleHeaderPointerDown below) -- no special handling needed on them. */
  header: React.ReactNode;
  /** Scrollable list content, rendered right after `header`. */
  children: React.ReactNode;
}

/**
 * Airbnb-style draggable bottom sheet with three snap points (collapsed /
 * media / full), built for /explore's mobile map+list view.
 *
 * Position is driven entirely by hand -- a plain `useMotionValue` (`sheetY`)
 * set directly from pointermove deltas while dragging, and `animate()`ed to
 * settle -- rather than framer-motion's own `drag` prop. Two things forced
 * that:
 *
 * 1. A second real drag gesture on the same drag-enabled element silently
 *    did nothing (`onDragStart` never fired again) unless a plain tap
 *    happened in between -- reproduced in complete isolation outside this
 *    app's code, and it matches framer/motion's own GitHub issue #712.
 * 2. framer's `drag` attaches its pointerdown listener natively on the DOM
 *    node itself, bypassing React's synthetic event system entirely -- so
 *    an interactive child inside a `drag`-enabled element (the header row's
 *    own Filters button) had its `click` silently eaten by framer's own
 *    handling, even with `e.stopPropagation()` on the button's own
 *    `onPointerDown` (that only stops other REACT handlers, not framer's
 *    native one on an ancestor).
 *
 * The move/up tracking itself is done via `window`-level listeners attached
 * imperatively from `onPointerDown` (and removed on pointerup/cancel),
 * rather than React's onPointerMove/onPointerUp props with element-level
 * `setPointerCapture`: capture interacted badly with this environment's
 * synthetic pointer events (drags reliably stopped mid-gesture after the
 * very first move), and a window listener is the conventional, more widely
 * compatible way to track a manual drag regardless of where the pointer
 * physically ends up -- it doesn't depend on capture at all.
 *
 * The handle + header row (the "block") lives *inside* the scrollable area,
 * as its first child, rather than as a fixed row above it. That makes it
 * genuinely scrollable content -- it slides up and disappears behind the
 * fixed app header on its own via the browser's normal scrolling, and
 * reappears the same way once scrolled back to the top, with no bespoke
 * show/hide logic needed for it. A separate, minimal loose handle (just the
 * bar, no header text) floats fixed at the top of the sheet and fades in
 * only once the block has scrolled out of view, so there's still something
 * to grab -- see the `looseHandleVisible` render below.
 */
const SnapBottomSheet = forwardRef<HTMLDivElement, SnapBottomSheetProps>(
  ({ containerHeight, headerClearance, snapPoint, onSnapPointChange, hidden, onListScrolledChange, header, children }, listRef) => {
    const { token } = theme.useToken();
    const isDraggingRef = React.useRef(false);

    // Tracks the list's own scrollTop crossing to/from 0 -- drives both the
    // loose handle's fade (below, 'full'/'media' only -- see
    // looseHandleVisible) and, via onListScrolledChange, the caller's
    // floating "Map" button (which only ever shows in 'full' on its own
    // account). Tracked unconditionally regardless of snapPoint since
    // that's simplest and harmless -- the list's own maxHeight below is
    // what actually gives 'media' (and, marginally, 'collapsed') real
    // scroll room to begin with.
    const [listScrolled, setListScrolled] = React.useState(false);
    React.useEffect(() => {
      const list = (listRef as React.RefObject<HTMLDivElement> | null)?.current;
      if (!list) return;
      const onScroll = () => setListScrolled(list.scrollTop > 0);
      list.addEventListener('scroll', onScroll, { passive: true });
      return () => list.removeEventListener('scroll', onScroll);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(() => {
      onListScrolledChange?.(listScrolled);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listScrolled]);

    // Leaving 'full' (drag, tap-to-collapse, the "Map" button...) always
    // resets scroll back to the top. Without this, collapsing/going to
    // media after having scrolled the list would show whatever result cards
    // were currently at the top of the viewport instead of the handle+
    // header block -- the block is normal scrollable content now (see the
    // component doc comment), so its visibility in collapsed/media depends
    // on scrollTop being 0, same as it does at the top of 'full'.
    React.useEffect(() => {
      if (snapPoint === 'full') return;
      const list = (listRef as React.RefObject<HTMLDivElement> | null)?.current;
      if (list) list.scrollTop = 0;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapPoint]);

    // The loose handle only needs to exist once the block has actually
    // scrolled out of view -- same signal as the caller's own "Map" button,
    // so the two stay in sync for free rather than needing separate
    // bookkeeping. 'collapsed' is excluded even now that the list can
    // technically scroll there too (see the list's own maxHeight below) --
    // its visible sliver is only ever about as tall as the block itself, so
    // there's nothing meaningful to scroll to in the first place.
    const looseHandleVisible = (snapPoint === 'full' || snapPoint === 'media') && listScrolled;

    // Measures the handle+header block's real rendered height, so the
    // 'collapsed' snap point can match it exactly instead of guessing a
    // fixed number -- avoids either clipping the block or leaving dead
    // space below it regardless of locale/content changes.
    const blockRef = React.useRef<HTMLDivElement>(null);
    const [peekHeight, setPeekHeight] = React.useState(SHEET_COLLAPSED_PEEK);
    React.useLayoutEffect(() => {
      const block = blockRef.current;
      if (!block) return;
      const measure = () => setPeekHeight(block.offsetHeight);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(block);
      return () => observer.disconnect();
    }, []);

    const snapOffsets: Record<SheetSnapPoint, number> = React.useMemo(
      () => ({
        // Slides the sheet's top edge right up against the fixed header
        // instead of behind it (unlike 'media'/'collapsed', which have no
        // reason to reserve that space at all -- see the component doc
        // comment).
        full: headerClearance,
        media: containerHeight * MEDIA_SNAP_RATIO,
        collapsed: Math.max(containerHeight - peekHeight, headerClearance),
      }),
      [containerHeight, headerClearance, peekHeight]
    );

    const sheetY = useMotionValue(snapOffsets[snapPoint]);

    // Programmatic snap changes (pin tap, mount, container/peek resize) --
    // guarded against fighting an in-progress drag, which drives sheetY
    // directly and settles it itself once released (see settleDrag below).
    React.useEffect(() => {
      if (isDraggingRef.current) return;
      animate(sheetY, snapOffsets[snapPoint], SPRING);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapPoint, containerHeight, headerClearance, peekHeight]);

    const clampY = (value: number) => Math.min(snapOffsets.collapsed, Math.max(headerClearance, value));

    const nearestSnap = (current: number, velocity: number): SheetSnapPoint => {
      // Closest-by-distance first, always -- a fast flick that already
      // travelled multiple stops (e.g. full all the way to collapsed) must
      // land on the stop it actually reached, not just one step from
      // wherever the drag *started*. A strong flick only nudges the result
      // one stop *further* in its own direction from that closest point
      // (e.g. a firm-but-short flick right at the midpoint between media and
      // collapsed commits to collapsed instead of settling back to media).
      let closestIndex = 0;
      let closestDist = Infinity;
      SNAP_ORDER.forEach((key, index) => {
        const dist = Math.abs(snapOffsets[key] - current);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = index;
        }
      });
      if (Math.abs(velocity) > FLICK_VELOCITY) {
        const nextIndex = velocity > 0 ? closestIndex + 1 : closestIndex - 1;
        if (nextIndex >= 0 && nextIndex < SNAP_ORDER.length) return SNAP_ORDER[nextIndex];
      }
      return SNAP_ORDER[closestIndex];
    };

    // Timestamped samples of the last couple of pointer positions, so
    // velocity can be estimated at release time -- window pointerup doesn't
    // hand us one directly the way framer's PanInfo did.
    const lastSampleRef = React.useRef<{ y: number; t: number } | null>(null);
    const velocityRef = React.useRef(0);

    const settleDrag = () => {
      isDraggingRef.current = false;
      const target = nearestSnap(sheetY.get(), velocityRef.current);
      animate(sheetY, snapOffsets[target], SPRING);
      if (target !== snapPoint) onSnapPointChange(target);
    };

    // Shared manual pointer-drag tracking, used by both the handle/header
    // zone and the list's scroll-to-drag handoff below. Movement and release
    // are tracked via window listeners (see the component doc comment)
    // rather than element props, so a drag keeps working no matter where the
    // pointer ends up relative to the element it started on.
    const pointerStartYRef = React.useRef<number | null>(null);
    const dragStartYRef = React.useRef(0);
    const activeRef = React.useRef(false);
    const cleanupRef = React.useRef<(() => void) | null>(null);
    // Total pointer travel this gesture, regardless of whether it ever
    // crossed canStart's own threshold -- lets handleHeaderClick tell a tap
    // from a drag that happened to settle back where it started (see its
    // own doc comment below).
    const dragMovedRef = React.useRef(false);

    const beginTracking = (startClientY: number, canStart: (deltaY: number) => boolean) => {
      pointerStartYRef.current = startClientY;
      activeRef.current = false;
      dragMovedRef.current = false;
      lastSampleRef.current = { y: startClientY, t: performance.now() };
      velocityRef.current = 0;

      const onMove = (event: PointerEvent) => {
        if (pointerStartYRef.current == null) return;
        const deltaY = event.clientY - pointerStartYRef.current;
        if (Math.abs(deltaY) > TAP_MOVE_THRESHOLD) dragMovedRef.current = true;
        if (!activeRef.current) {
          if (!canStart(deltaY)) return;
          activeRef.current = true;
          isDraggingRef.current = true;
          dragStartYRef.current = sheetY.get();
        }
        const now = performance.now();
        const last = lastSampleRef.current;
        if (last) {
          const dt = now - last.t;
          if (dt > 0) velocityRef.current = ((event.clientY - last.y) / dt) * 1000;
        }
        lastSampleRef.current = { y: event.clientY, t: now };
        sheetY.set(clampY(dragStartYRef.current + deltaY));
      };

      const onUp = () => {
        if (activeRef.current) settleDrag();
        pointerStartYRef.current = null;
        activeRef.current = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        cleanupRef.current = null;
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
      cleanupRef.current = onUp;
    };

    React.useEffect(() => () => cleanupRef.current?.(), []);

    // Handle/header zone: always eligible to start a drag, except when the
    // pointer came down on (or inside) a button -- so a tap there reaches
    // its own onClick normally. stopPropagation matters now that this zone
    // is nested inside the scrollable list (see the component doc comment)
    // -- without it, the same pointerdown would also bubble into
    // handleListPointerDown below and start a second, redundant drag-track.
    const handleHeaderPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest('button')) return;
      event.stopPropagation();
      beginTracking(event.clientY, () => true);
    };

    // A plain tap (no real drag) on the handle/header block steps between
    // *adjacent* snap points, one direction only -- collapsed jumps up to
    // full, full drops back to media, and media itself has no tap target
    // (it's reached only by dragging, from either neighbour). This is an
    // added shortcut layered on top of the drag gesture, never a
    // replacement for it, so it only fires when dragMovedRef never crossed
    // TAP_MOVE_THRESHOLD during this pointer-down/up cycle -- an actual drag
    // that happens to settle back on its start point must NOT also trigger
    // this.
    const handleHeaderClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest('button')) return;
      if (dragMovedRef.current) return;
      if (snapPoint === 'collapsed') onSnapPointChange('full');
      else if (snapPoint === 'full') onSnapPointChange('media');
    };

    // List: only hands off into a sheet drag once the list truly has
    // nothing left to scroll up into (scrollTop === 0) AND the gesture is
    // pulling down -- otherwise this would hijack an ordinary downward
    // scroll anywhere else in the list.
    const handleListPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
      beginTracking(event.clientY, (deltaY) => {
        const list = (listRef as React.RefObject<HTMLDivElement> | null)?.current;
        return !!list && list.scrollTop <= 0 && deltaY > 6;
      });
    };

    return (
      <motion.div
        style={{
          y: sheetY,
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: containerHeight,
          boxSizing: 'border-box',
          background: token.colorBgContainer,
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.16)',
          display: 'flex',
          flexDirection: 'column',
          // Explicit, not left at the implicit "auto" stacking level a plain
          // position:absolute element gets -- a sibling with its own
          // explicit z-index (ExploreMobileMap's MapCategoryPicker, at 45)
          // always paints above an "auto" element regardless of DOM order,
          // which let that category pill bleed on top of this sheet's own
          // cards once dragged up past it. Above the category picker (45),
          // below ExploreMobileMap's own fixed header (50) so the header
          // still wins even at the 'full' snap point.
          zIndex: 46,
          // Re-enables hit-testing for the sheet itself -- its container
          // (see ExploreMobileMap) sets pointerEvents: 'none' on the
          // wrapper around this, since that wrapper's own box spans the
          // whole map area regardless of the sheet's current position and
          // would otherwise swallow every tap on the map underneath. Only
          // whatever part of this element is actually visible ends up
          // hit-testable in practice, since the rest is clipped away by the
          // outer container's own overflow: hidden. Overridden to 'none'
          // by `hidden` (see its own doc comment) so a hidden sheet can't
          // still be dragged/tapped behind an open pin popup.
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? 'none' : 'auto',
          transition: 'opacity 200ms ease',
        }}
      >
        {/* Loose handle -- fixed at the top of the sheet, independent of
            scroll. Fades in only once the in-flow block (below) has
            scrolled out of view, so there's always something to grab even
            deep into the list -- see looseHandleVisible above. A soft
            gradient backing keeps it legible over whatever result card is
            currently scrolled underneath it without reading as a second
            solid header bar. */}
        <div
          onPointerDown={looseHandleVisible ? handleHeaderPointerDown : undefined}
          onClick={looseHandleVisible ? handleHeaderClick : undefined}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            padding: '10px 0 16px',
            background: `linear-gradient(${token.colorBgContainer} 55%, transparent)`,
            cursor: 'grab',
            touchAction: 'none',
            userSelect: 'none',
            zIndex: 2,
            opacity: looseHandleVisible ? 1 : 0,
            pointerEvents: looseHandleVisible ? 'auto' : 'none',
            transition: 'opacity 200ms ease',
          }}
        >
          <div style={{ width: 36, height: 4, borderRadius: 2, background: token.colorBorder }} />
        </div>

        <div
          ref={listRef}
          onPointerDown={handleListPointerDown}
          style={{
            flex: 1,
            minHeight: 0,
            // The sheet's own box is always containerHeight tall regardless
            // of snapPoint (see the component doc comment on why -- cheap
            // translate-only dragging) -- without this, the list's content
            // usually just fits inside that full height with nothing left
            // to scroll, even though most of that height sits clipped below
            // the *visible* window at 'media'/'collapsed'. Native scroll can
            // only ever reveal overflow within an element's own box, never
            // content an ancestor is clipping -- capping this to what's
            // actually visible at the current snap point is what gives it
            // real content to scroll through. Recomputed on snapPoint
            // settling, not smoothly during an active drag (sheetY changes
            // every frame then) -- a plain layout property like this isn't
            // worth animating at drag speed, and it's not read from the
            // list during a drag anyway (dragging and scrolling are already
            // mutually exclusive gestures, handled below).
            maxHeight: containerHeight - snapOffsets[snapPoint],
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
          }}
        >
          {/* The handle+header block -- first item of the sheet's own
              scrollable content (not a fixed row above it), so it slides
              away under the fixed app header on ordinary scroll and comes
              back the same way at scrollTop 0, with no extra show/hide
              logic of its own -- see the component doc comment. */}
          <div ref={blockRef} onPointerDown={handleHeaderPointerDown} onClick={handleHeaderClick} style={{ cursor: 'grab', touchAction: 'none', userSelect: 'none' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: token.colorBorder, margin: '8px auto 0' }} />
            {header}
          </div>
          {children}
        </div>
      </motion.div>
    );
  }
);

export default SnapBottomSheet;
